import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 20  -  Writing a Lab Report
// ============================================

/** 20.1 – Report Structure Overview */
export const ReportStructure = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Structure of a Scientific Lab Report", W / 2, 24);

    // Sections as vertical flow
    const sections = [
      { name: "Title & Authors", desc: "Clear, descriptive title\nAll team members listed", color: "#3B82F6", icon: "📄" },
      { name: "Abstract", desc: "Brief summary (~150 words)\nGoal, method, key result", color: "#8B5CF6", icon: "📋" },
      { name: "Introduction", desc: "Physics motivation\nWhat question are we answering?", color: "#22C55E", icon: "❓" },
      { name: "Method / Setup", desc: "Equipment, procedure\nReproducible description", color: "#F59E0B", icon: "⚙️" },
      { name: "Results", desc: "Data, plots, fits\nWhat did you measure?", color: "#EF4444", icon: "📊" },
      { name: "Discussion", desc: "Interpret results\nCompare to theory / literature", color: "#EC4899", icon: "💡" },
      { name: "Conclusion", desc: "Summary of findings\nFuture improvements", color: "#06B6D4", icon: "✅" },
      { name: "References", desc: "Cite all sources properly", color: "#64748B", icon: "📚" },
    ];

    const colW = 255,
      rowH = 28;
    sections.forEach((s, i) => {
      const col = i < 4 ? 0 : 1;
      const row = i < 4 ? i : i - 4;
      const x = 20 + col * 280;
      const y = 46 + row * 56;

      ctx.fillStyle = s.color + "10";
      ctx.beginPath();
      ctx.roundRect(x, y, colW, 48, 6);
      ctx.fill();
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x, y, colW, 48, 6);
      ctx.stroke();

      ctx.fillStyle = s.color;
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText((i + 1) + ". " + s.name, x + 10, y + 16);

      ctx.fillStyle = "#475569";
      ctx.font = "9px Inter,sans-serif";
      s.desc
        .split("\n")
        .forEach((l, li) => ctx.fillText(l, x + 10, y + 30 + li * 11));

      // Arrow to next (within same column)
      if (row < 3) {
        ctx.strokeStyle = "#CBD5E1";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + colW / 2, y + 48);
        ctx.lineTo(x + colW / 2, y + 56);
        ctx.stroke();
      }
    });

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Follow this structure for clarity  -  every physics paper uses it!",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 20.2 – Writing Abstract and Introduction */
export const AbstractIntro = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Writing the Abstract and Introduction", W / 2, 24);

    // Abstract card
    ctx.fillStyle = "#8B5CF610";
    ctx.beginPath();
    ctx.roundRect(20, 44, 245, 170, 10);
    ctx.fill();
    ctx.strokeStyle = "#8B5CF6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(20, 44, 245, 170, 10);
    ctx.stroke();

    ctx.fillStyle = "#8B5CF6";
    ctx.font = "bold 13px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Abstract", 142, 64);

    const absPoints = [
      "1-paragraph summary (100-200 words)",
      "State the goal of the experiment",
      "Briefly describe the method",
      "Give the KEY numerical result",
      "Include uncertainties!",
      "Write LAST  -  after everything else",
    ];
    absPoints.forEach((p, i) => {
      ctx.fillStyle = i === 5 ? "#8B5CF6" : "#475569";
      ctx.font = i === 5 ? "bold 10px Inter,sans-serif" : "10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("• " + p, 35, 84 + i * 18);
    });

    // Example
    ctx.fillStyle = "#F5F3FF";
    ctx.beginPath();
    ctx.roundRect(30, 180, 225, 25, 4);
    ctx.fill();
    ctx.fillStyle = "#7C3AED";
    ctx.font = "italic 9px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      '"We measured the muon lifetime as 2.1±0.2 μs..."',
      142,
      196
    );

    // Introduction card
    ctx.fillStyle = "#22C55E10";
    ctx.beginPath();
    ctx.roundRect(275, 44, 245, 170, 10);
    ctx.fill();
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(275, 44, 245, 170, 10);
    ctx.stroke();

    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 13px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Introduction", 397, 64);

    const introPoints = [
      "Start broad → narrow to your topic",
      "Physics context (what's known)",
      "What gap does your work address?",
      "Clearly state your objective",
      "Cite relevant prior work",
      "End with: 'In this report, we...'",
    ];
    introPoints.forEach((p, i) => {
      ctx.fillStyle = i === 5 ? "#22C55E" : "#475569";
      ctx.font = i === 5 ? "bold 10px Inter,sans-serif" : "10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("• " + p, 290, 84 + i * 18);
    });

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Both sections set the stage  -  reader should know WHAT and WHY after reading them",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 20.3 – Methodology and Setup Description */
export const MethodologySetup = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Describing Methodology and Setup", W / 2, 24);

    // Layout diagram concept
    ctx.fillStyle = "#F59E0B10";
    ctx.beginPath();
    ctx.roundRect(20, 45, 250, 175, 10);
    ctx.fill();
    ctx.strokeStyle = "#F59E0B";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(20, 45, 250, 175, 10);
    ctx.stroke();

    ctx.fillStyle = "#F59E0B";
    ctx.font = "bold 13px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Include a Setup Diagram", 145, 65);

    // Mini beamline sketch
    const by = 120;
    ctx.strokeStyle = "#94A3B8";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(40, by);
    ctx.lineTo(250, by);
    ctx.stroke();

    const items = [
      { x: 60, label: "S1", color: "#22C55E" },
      { x: 120, label: "C1", color: "#3B82F6" },
      { x: 180, label: "S2", color: "#22C55E" },
      { x: 230, label: "S3", color: "#EF4444" },
    ];
    items.forEach((it) => {
      ctx.fillStyle = it.color + "30";
      ctx.fillRect(it.x - 8, by - 15, 16, 30);
      ctx.strokeStyle = it.color;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(it.x - 8, by - 15, 16, 30);
      ctx.fillStyle = it.color;
      ctx.font = "bold 9px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(it.label, it.x, by + 28);
    });

    ctx.fillStyle = "#475569";
    ctx.font = "9px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Label every component with dimensions!", 145, 175);
    ctx.fillText("Include distances between detectors.", 145, 190);
    ctx.fillText("Specify materials, voltages, thresholds.", 145, 205);

    // Checklist (right side)
    ctx.fillStyle = "#3B82F610";
    ctx.beginPath();
    ctx.roundRect(285, 45, 255, 175, 10);
    ctx.fill();
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(285, 45, 255, 175, 10);
    ctx.stroke();

    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 13px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Methodology Checklist", 412, 65);

    const checks = [
      "Describe each detector (type, size, material)",
      "State beam properties (momentum, composition)",
      "Explain trigger logic (coincidence, veto)",
      "Detail calibration procedure & standards",
      "List DAQ settings (TDC bins, gate widths)",
      "Describe data selection / quality cuts",
      "Make it REPRODUCIBLE by another team",
    ];
    checks.forEach((ch, i) => {
      ctx.fillStyle = i === 6 ? "#3B82F6" : "#475569";
      ctx.font = i === 6 ? "bold 10px Inter,sans-serif" : "10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("☑ " + ch, 298, 84 + i * 18);
    });

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Gold standard: could someone replicate your experiment from your description alone?",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 20.4 – Data Presentation Best Practices */
export const DataPresentation = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Data Presentation  -  Best Practices", W / 2, 24);

    // Good plot vs bad plot
    const plotY = 50,
      plotH = 110,
      plotW2 = 210;

    // Good plot
    ctx.fillStyle = "#22C55E10";
    ctx.beginPath();
    ctx.roundRect(20, plotY, 250, 160, 8);
    ctx.fill();
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(20, plotY, 250, 160, 8);
    ctx.stroke();

    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("✓ Good Plot", 145, plotY + 16);

    // Mini axes
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(55, plotY + 30);
    ctx.lineTo(55, plotY + 130);
    ctx.lineTo(245, plotY + 130);
    ctx.stroke();

    // Axis labels
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 9px Inter,sans-serif";
    ctx.fillText("TOF (ns)", 150, plotY + 147);
    ctx.save();
    ctx.translate(35, plotY + 80);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText("Counts", 0, 0);
    ctx.restore();

    // Data with error bars
    const pts = [
      [0.15, 0.2],
      [0.3, 0.45],
      [0.45, 0.8],
      [0.55, 0.6],
      [0.7, 0.35],
      [0.85, 0.15],
    ];
    pts.forEach(([x, y]) => {
      const px = 55 + x * 190;
      const py = plotY + 130 - y * 95;
      ctx.fillStyle = "#1E293B";
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#1E293B";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(px, py - 8);
      ctx.lineTo(px, py + 8);
      ctx.stroke();
    });

    // Fit curve
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let px = 0; px < 190; px++) {
      const x = px / 190;
      const y = 0.85 * Math.exp(-0.5 * Math.pow((x - 0.45) / 0.12, 2));
      const dy = plotY + 130 - y * 95;
      if (px === 0) ctx.moveTo(55 + px, dy);
      else ctx.lineTo(55 + px, dy);
    }
    ctx.stroke();

    ctx.fillStyle = "#475569";
    ctx.font = "8px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Legend: �-� data   -  fit", 60, plotY + 158);

    // Bad plot
    ctx.fillStyle = "#EF444410";
    ctx.beginPath();
    ctx.roundRect(290, plotY, 250, 160, 8);
    ctx.fill();
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(290, plotY, 250, 160, 8);
    ctx.stroke();

    ctx.fillStyle = "#EF4444";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("�- Bad Plot", 415, plotY + 16);

    // No axes labels, no error bars, connected dots
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(320, plotY + 30);
    ctx.lineTo(320, plotY + 130);
    ctx.lineTo(510, plotY + 130);
    ctx.stroke();

    ctx.strokeStyle = "#94A3B8";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    pts.forEach(([x, y], i) => {
      const px = 320 + x * 190;
      const py = plotY + 130 - y * 95;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();

    const issues = ["No axis labels!", "No error bars!", "No units!", "No legend!", "No fit shown!"];
    issues.forEach((iss, i) => {
      ctx.fillStyle = "#EF4444";
      ctx.font = "9px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("\u2717 " + iss, 312, plotY + 120 + i * 12);
    });

    // Rules
    const rules = [
      { rule: "Label axes with units", icon: "📏" },
      { rule: "Show error bars always", icon: "⊥" },
      { rule: "Add legend and fit info", icon: "📝" },
      { rule: "Caption every figure", icon: "💬" },
      { rule: "Number figures: Fig. 1, ...", icon: "#" },
    ];
    rules.forEach((r, i) => {
      const col = i % 3, row = Math.floor(i / 3);
      ctx.fillStyle = "#1E293B";
      ctx.font = "10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(r.icon + " " + r.rule, 30 + col * 180, H - 22 + row * 14);
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-red-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 20.5 – Error Analysis Section */
export const ErrorAnalysis = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Error Analysis in Your Report", W / 2, 24);

    // Error propagation formula
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 14px Inter,sans-serif";
    ctx.fillText("σ_f = √( Σ (∂f/∂xᵢ · σ_xᵢ)² )", W / 2, 52);

    // Three main categories
    const cats = [
      {
        title: "Identify Sources",
        items: [
          "TDC resolution (±25 ps)",
          "Path length uncertainty",
          "Momentum spread (Δp/p)",
          "Alignment/positioning",
        ],
        color: "#3B82F6",
      },
      {
        title: "Quantify Each",
        items: [
          "Measure or estimate σ",
          "Propagate through formula",
          "Use partial derivatives",
          "Add in quadrature",
        ],
        color: "#F59E0B",
      },
      {
        title: "Report Properly",
        items: [
          "m = 105.7 ± 0.3 ± 0.1 MeV",
          "State dominant source",
          "Compare to known value",
          "Discuss improvements",
        ],
        color: "#22C55E",
      },
    ];

    cats.forEach((cat, i) => {
      const x = 15 + i * 178,
        y = 68,
        w = 168,
        h = 128;
      ctx.fillStyle = cat.color + "08";
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.fill();
      ctx.strokeStyle = cat.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.stroke();

      ctx.fillStyle = cat.color;
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText((i + 1) + ". " + cat.title, x + w / 2, y + 18);

      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      ctx.textAlign = "left";
      cat.items.forEach((item, j) => {
        ctx.fillText("• " + item, x + 8, y + 36 + j * 22);
      });

      // Arrow to next
      if (i < 2) {
        ctx.fillStyle = "#94A3B8";
        ctx.font = "16px Inter,sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("→", x + w + 5, y + h / 2);
      }
    });

    // Example error budget
    ctx.fillStyle = "#FFFBEB";
    ctx.beginPath();
    ctx.roundRect(30, 205, 480, 44, 8);
    ctx.fill();
    ctx.fillStyle = "#92400E";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Example error budget for TOF mass measurement:",
      W / 2,
      222
    );
    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText(
      "σ_m/m = √[(σ_p/p)² + (γ²·c·σ_t/L)² + (γ²·β²·σ_L/L)²]   (dominant: timing!)",
      W / 2,
      240
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 20.6 – Citations and Referencing */
export const CitationReferencing = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Citations and Referencing", W / 2, 24);

    // Types of references
    const types = [
      {
        title: "Journal Article",
        example: 'Author et al., "Title", Journal Vol, Page (Year)',
        format: "[1] G. Bennett et al., Phys. Rev. D 73, 072003 (2006)",
        color: "#3B82F6",
      },
      {
        title: "Book / Textbook",
        example: "Author, Title, Publisher, Edition (Year)",
        format: '[2] D. Griffiths, "Intro to Elementary Particles", Wiley (2008)',
        color: "#22C55E",
      },
      {
        title: "Web / PDG",
        example: "Organization, URL (accessed Date)",
        format: "[3] PDG, pdg.lbl.gov, accessed Jan 2025",
        color: "#F59E0B",
      },
      {
        title: "BL4S Materials",
        example: "BL4S Collaboration, Document (Year)",
        format: "[4] BL4S 2026 Guidelines, beamline-for-schools.cern",
        color: "#8B5CF6",
      },
    ];

    types.forEach((t, i) => {
      const y = 48 + i * 44;
      ctx.fillStyle = t.color + "08";
      ctx.beginPath();
      ctx.roundRect(20, y, 500, 38, 6);
      ctx.fill();
      ctx.strokeStyle = t.color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(20, y, 500, 38, 6);
      ctx.stroke();

      ctx.fillStyle = t.color;
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(t.title, 30, y + 15);

      ctx.fillStyle = "#475569";
      ctx.font = "9px Inter,sans-serif";
      ctx.fillText(t.format, 30, y + 30);
    });

    // Do's and Don'ts
    const ly = 228;
    ctx.fillStyle = "#22C55E10";
    ctx.beginPath();
    ctx.roundRect(20, ly - 8, 245, 35, 6);
    ctx.fill();
    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("✓ Cite every claim, number, figure", 30, ly + 6);
    ctx.fillText("✓ Use numbered [N] style consistently", 30, ly + 20);

    ctx.fillStyle = "#EF444410";
    ctx.beginPath();
    ctx.roundRect(275, ly - 8, 245, 35, 6);
    ctx.fill();
    ctx.fillStyle = "#EF4444";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.fillText("�- Don't cite Wikipedia as primary source", 285, ly + 6);
    ctx.fillText("�- Don't copy text without attribution", 285, ly + 20);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 20.7 – BL4S Proposal Tips (case study) */
export const BL4SProposalTips = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("BL4S Proposal  -  Winning Tips", W / 2, 24);

    // Sections with tips
    const tips = [
      {
        section: "Physics Case",
        advice: "Clear question + why T9 is needed\nShow you understand the physics",
        weight: "30%",
        color: "#3B82F6",
      },
      {
        section: "Experimental Design",
        advice: "Realistic setup + feasibility\nUse available detectors wisely",
        weight: "30%",
        color: "#22C55E",
      },
      {
        section: "Analysis Plan",
        advice: "Expected signals + how to extract\nEstimate backgrounds & uncertainties",
        weight: "20%",
        color: "#F59E0B",
      },
      {
        section: "Team & Outreach",
        advice: "Show enthusiasm + teamwork\nPlan for sharing results publicly",
        weight: "20%",
        color: "#8B5CF6",
      },
    ];

    tips.forEach((t, i) => {
      const col = i < 2 ? 0 : 1;
      const row = i % 2;
      const x = 20 + col * 270;
      const y = 48 + row * 90;
      const w = 260;
      const h = 80;

      ctx.fillStyle = t.color + "08";
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.fill();
      ctx.strokeStyle = t.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.stroke();

      // Weight badge
      ctx.fillStyle = t.color;
      ctx.beginPath();
      ctx.roundRect(x + w - 45, y + 5, 38, 18, 4);
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(t.weight, x + w - 26, y + 18);

      ctx.fillStyle = t.color;
      ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(t.section, x + 10, y + 20);

      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      t.advice
        .split("\n")
        .forEach((l, li) => ctx.fillText(l, x + 10, y + 38 + li * 14));
    });

    // Golden rules
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath();
    ctx.roundRect(20, 235, 520, 38, 8);
    ctx.fill();
    ctx.fillStyle = "#92400E";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Golden rules: Be specific, be realistic, show passion, and proofread everything!",
      W / 2,
      252
    );
    ctx.fillStyle = "#78350F";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText(
      "Proposals are 3000 words max  -  every sentence must count.",
      W / 2,
      268
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};
